import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { CheckCircle, Loader2, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CheckoutPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [checking, setChecking] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [trialStarted, setTrialStarted] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(location.state?.plan || 'monthly');

  const checkPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error('Payment verification timed out. Please contact support.');
      return;
    }

    setChecking(true);

    try {
      const response = await axios.get(
        `${API}/subscription/status/${sessionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.payment_status === 'paid') {
        setPaymentSuccess(true);
        await refreshUser();
        toast.success('Payment added! Your subscription is now active.');
        setTimeout(() => navigate('/sanctuary'), 2000);
        return;
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        setChecking(false);
        return;
      }

      setTimeout(() => checkPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      toast.error('Failed to verify payment');
      setChecking(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, navigate, refreshUser]);

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [sessionId, checkPaymentStatus]);

  // Start free trial WITHOUT credit card
  const handleStartTrial = async (plan) => {
    setLoading(true);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(
        `${API}/subscription/start-trial`,
        { origin_url: originUrl, plan: plan },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setTrialStarted(true);
        await refreshUser();
        toast.success('Your 14-day free trial has started!');
        setTimeout(() => navigate('/sanctuary'), 2500);
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to start trial';
      toast.error(message);
      setLoading(false);
    }
  };

  // Success states
  if (checking || paymentSuccess || trialStarted) {
    const renderSuccessContent = () => {
      if (paymentSuccess) {
        return (
          <>
            <CheckCircle className="w-20 h-20 text-green-600 mx-auto mb-6" />
            <h2 className="text-3xl font-medium mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Payment Added!
            </h2>
            <p className="text-stone-600">Redirecting to your sanctuary...</p>
          </>
        );
      }
      if (trialStarted) {
        return (
          <>
            <Sparkles className="w-20 h-20 mx-auto mb-6" style={{ color: '#C9A84C' }} />
            <h2 className="text-3xl font-medium mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Your Free Trial Has Begun!
            </h2>
            <p className="text-stone-600 mb-2">14 days of full access — no credit card required.</p>
            <p className="text-stone-500 text-sm">Redirecting to your sanctuary...</p>
          </>
        );
      }
      return (
        <>
          <Loader2 className="w-20 h-20 text-stone-600 mx-auto mb-6 animate-spin" />
          <h2 className="text-3xl font-medium mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Verifying Payment...
          </h2>
          <p className="text-stone-600">Please wait while we confirm your subscription</p>
        </>
      );
    };

    return (
      <div className="min-h-screen bg-[#FBF9F6] flex items-center justify-center px-6">
        <div className="text-center">
          {renderSuccessContent()}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FBF9F6] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-4xl">
        <div className="text-center mb-12">
          <div 
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-6"
            style={{ backgroundColor: 'rgba(201,168,76,0.15)', color: '#a08242' }}
          >
            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
            14-Day Free Trial — No Credit Card Required
          </div>
          <h1
            className="text-4xl md:text-5xl font-medium tracking-tight text-stone-800 mb-4"
            style={{ fontFamily: 'Playfair Display, serif' }}
            data-testid="checkout-heading"
          >
            Your healing starts today
          </h1>
          <p className="text-lg text-stone-600">
            Start your 14-day free trial instantly.<br />
            No credit card needed. Cancel anytime.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Monthly Plan */}
          <div 
            className={`bg-white/80 backdrop-blur-md border-2 shadow-lg rounded-3xl p-8 cursor-pointer transition-all duration-300 ${
              selectedPlan === 'monthly' ? 'border-stone-800 scale-[1.02]' : 'border-stone-200'
            }`}
            onClick={() => setSelectedPlan('monthly')}
            data-testid="plan-monthly"
          >
            <div className="text-center mb-6">
              <div className="text-sm font-medium text-stone-500 mb-2">MONTHLY</div>
              <div className="text-4xl font-medium mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>
                $37<span className="text-xl text-stone-500">/month</span>
              </div>
              <p className="text-stone-500 text-sm">After trial ends</p>
            </div>
            
            <ul className="space-y-3 mb-6 text-sm text-stone-600">
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                All 4 healing phases
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                Daily personalized prompts
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                Unlimited Sage conversations
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                Journal & progress tracking
              </li>
            </ul>

            <Button
              onClick={() => handleStartTrial('monthly')}
              disabled={loading}
              variant="outline"
              className="w-full rounded-full px-8 py-5 text-base border-stone-300 hover:bg-stone-50 transition-all duration-300"
              data-testid="checkout-monthly-button"
            >
              {loading && selectedPlan === 'monthly' ? 'Starting Trial...' : 'Start Free Trial'}
            </Button>
          </div>

          {/* Annual Plan */}
          <div 
            className={`bg-white/80 backdrop-blur-md border-2 shadow-xl rounded-3xl p-8 relative cursor-pointer transition-all duration-300 ${
              selectedPlan === 'annual' ? 'scale-[1.02]' : ''
            }`}
            style={{ borderColor: selectedPlan === 'annual' ? '#C9A84C' : '#fbbf24' }}
            onClick={() => setSelectedPlan('annual')}
            data-testid="plan-annual"
          >
            <div 
              className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-medium text-[#1A1A2E]"
              style={{ backgroundColor: '#C9A84C' }}
            >
              MOST CHOSEN
            </div>
            
            <div className="text-center mb-6">
              <div className="text-sm font-medium mb-2" style={{ color: '#C9A84C' }}>ANNUAL</div>
              <div className="text-4xl font-medium mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>
                $333<span className="text-xl text-stone-500">/year</span>
              </div>
              <p className="text-sm" style={{ color: '#C9A84C' }}>
                $27.75/month — Save $111
              </p>
            </div>
            
            <ul className="space-y-3 mb-6 text-sm text-stone-600">
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                Everything in Monthly
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4" style={{ color: '#C9A84C' }} />
                <span style={{ color: '#C9A84C' }}>3 months free — pay for 9, get 12</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                48-hour support response guarantee
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                Downloadable workbooks for each healing phase
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                Private member circle — weekly prompts + peer support
              </li>
            </ul>

            <Button
              onClick={() => handleStartTrial('annual')}
              disabled={loading}
              className="w-full rounded-full px-8 py-5 text-base text-[#1A1A2E] shadow-lg transition-all duration-300 hover:scale-[1.02]"
              style={{ backgroundColor: '#C9A84C' }}
              data-testid="checkout-annual-button"
            >
              {loading && selectedPlan === 'annual' ? 'Starting Trial...' : 'Start Free Trial — Save $111/year'}
            </Button>
          </div>
        </div>

        <div className="text-center space-y-2">
          <p className="text-sm text-stone-500">
            No credit card required. We'll remind you before your trial ends.
          </p>
          <p className="text-xs text-stone-400">
            14-day free trial with full access. Add payment anytime in{' '}
            <button
              type="button"
              onClick={() => navigate('/settings')}
              className="underline underline-offset-2 hover:text-stone-600 transition-colors font-medium"
              data-testid="checkout-open-settings-link"
            >
              Settings
            </button>
            .
          </p>
          <p className="text-xs text-stone-400 mt-2">
            <strong>Refund Policy:</strong> 7-day refund from first charge. Annual plans prorated within 30 days.
          </p>
        </div>
      </div>
    </div>
  );
};
